// @flow

import React from 'react';
import { faEnvelope } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LocalForage from 'localforage';
import { Alert } from 'react-bootstrap';
import { compose } from 'redux';
import { Button, Loader } from 'Components';
import { translate, withAuth } from 'Hoc';
import { getAuthError, swapToken } from '../Services/Auth';
import { pursueLogin } from '../Services/Login';
import {
  checkedEmailKey,
  getSsoToken,
  signInWithSocialMedia,
  SocialMedia,
  ssoLogout,
} from '../Services/SSO';
import LoginForm from './LoginForm';
import Styles from './Stylesheets/LoginMethodSelector.module.css';

const State = {
  initial: 'initial',
  email: 'email',
  signInSocialMediaSuccess: 'signInSocialMediaSuccess',
};

type LoginMethodSelectorPropsType = {
  t: string => string,
  setPartnerHeaderCode: string => void,
};
type LoginMethodSelectorStateType = {
  state: $Values<typeof State>,
  mutationError: ?ErrorType,
};

class LoginMethodSelector extends React.PureComponent<
  LoginMethodSelectorPropsType,
  LoginMethodSelectorStateType,
> {
  constructor(props) {
    super(props);

    this.state = {
      state: State.initial,
      mutationError: undefined,
    };
  }

  handleCloseAlert = () => this.setState({ mutationError: undefined });

  handleAuthError = (error: Error) => {
    const { t } = this.props;

    this.setState({ mutationError: getAuthError(t, error) });
  };

  loginSocialMedia = async (socialMedia: $Values<typeof SocialMedia>) => {
    let ssoToken = null;

    await LocalForage.removeItem(checkedEmailKey);
    try {
      await signInWithSocialMedia(socialMedia);
      ssoToken = await getSsoToken();
    } catch (error) {
      console.warn('error', error);

      return;
    }

    this.setState({ state: State.signInSocialMediaSuccess });

    try {
      await swapToken(ssoToken);
      const { setPartnerHeaderCode } = this.props;
      const partnerCode = await pursueLogin();
      await setPartnerHeaderCode(partnerCode);
    } catch (error) {
      this.handleAuthError(error);
      this.setState({ state: State.initial });
      await ssoLogout();
    }
  };

  render() {
    const { t } = this.props;
    const { state, mutationError } = this.state;

    const LoginButton = ({ children, ...props }) => (
      <Button
        className={[
          'font-weight-light',
          state === State.signInSocialMediaSuccess ? 'd-none' : 'd-flex',
          'justify-content-center',
          'align-items-center',
          'w-100 mb-2',
          Styles.button,
        ].join(' ')}
        variant="outline-primary"
        {...props}
      >
        {children}
      </Button>
    );

    if (state === State.email) {
      return (
        <>
          <Button
            className={['p-0 mb-1', Styles.link].join(' ')}
            variant="link"
            onClick={e => {
              e.preventDefault();
              this.setState({ state: State.initial });
            }}
          >
            <FontAwesomeIcon icon="chevron-left" className="mr-1" />
            <u>{t('login_form:back')}</u>
          </Button>
          <LoginForm />
        </>
      );
    }

    return (
      <>
        <Alert
          dismissible
          variant="danger"
          show={typeof mutationError !== 'undefined'}
          onClose={this.handleCloseAlert}
        >
          <Alert.Heading>{t('form_errors:errorOccurred')}</Alert.Heading>
          <p>{mutationError ? mutationError.message : ''}</p>
        </Alert>
        {state === State.signInSocialMediaSuccess && (
          <div className="d-flex flex-column align-items-center">
            <div className="text-primary">{t('login_form:loadingMessage')}</div>
            <Loader color="info" />
          </div>
        )}
        <LoginButton onClick={() => this.setState({ state: State.email })}>
          <FontAwesomeIcon icon={faEnvelope} className="mr-1" />
          {t('login_form:email')}
        </LoginButton>
        <LoginButton type="submit" onClick={() => this.loginSocialMedia(SocialMedia.google)}>
          <img
            src={t('login_method_selector:googleIcon')}
            alt="google-icon"
            className={['mr-1', Styles.icon].join(' ')}
          />
          {t('login_form:google')}
        </LoginButton>
      </>
    );
  }
}

export default compose(
  withAuth(),
  translate(['login_form', 'login_method_selector', 'form_errors', 'gql_errors']),
)(LoginMethodSelector);
